import _ from 'underscore';
import { underscored } from 'underscore.string';
import Backbone from '@polleverywhere/backbone';
import env from '@polleverywhere/core/lib/env';
import { batchCallFunction } from '@polleverywhere/core/lib/util';
var DATADOG_METRIC_FORMAT = /^[a-zA-Z][.\w]+$/;

var isValidMetricName = function isValidMetricName(name) {
  return DATADOG_METRIC_FORMAT.test(name);
};

var taggerize = function taggerize(tags) {
  // add the additional tags
  tags = function () {
    var result = [];

    for (var name in tags) {
      var value = tags[name];

      if (!_.isEmpty(value)) {
        name = underscored(name);
        result.push(name + ":" + value);
      } else {
        result.push(undefined);
      }
    }

    return result;
  }();

  return _.compact(tags);
};

var addTimestampsToPoints = function addTimestampsToPoints(data) {
  var timestamp = Math.floor(Date.now() / 1000);
  data.points = _.map(data.points, function (x) {
    return [timestamp, x];
  });
};

var postToDatadog = function postToDatadog(self, action, data) {
  if (!data.length) {
    return;
  }

  self.app.trigger('log:debug', 'DataDog metric', data);
  /**
   * TODO: for some reason, env is undefined when running CI
   * Need to look into why it's set to undefined in some cases
   * although it should not be
   */

  var url = env != null ? env.get('ddag_url') : undefined;

  if (url) {
    return Backbone.ajax({
      type: 'post',
      dataType: 'json',
      url: url.segment(action).toString(),
      data: JSON.stringify(data),
      proxy: 'none'
    });
  }
};

var getBrowserInfo = function getBrowserInfo(ua) {
  var temp;
  var M = ua.match(/(pollevpresenter|opera|chrome|safari|firefox|msie|trident(?=\/))\/?\s*([0-9.]+)/i) || [];

  if (/trident/i.test(M[1])) {
    temp = /\brv[ :]+(\d+)/g.exec(ua) || [];
    return {
      name: 'IE',
      version: temp[1] || ''
    };
  }

  if (M[1] === 'Chrome') {
    temp = ua.match(/\bOPR\/(\d+)/);

    if (temp) {
      return {
        name: 'Opera',
        version: temp[1]
      };
    }
  }

  M = M[2] ? [M[1], M[2]] : [ua, '?'];
  temp = ua.match(/version\/(\d+)/i);

  if (temp) {
    M.splice(1, 1, temp[1]);
  }

  return {
    name: M[0],
    version: M[1]
  };
};

var toDatadogMetricFormat = function toDatadogMetricFormat(self, metricData, tags) {
  if (tags === void 0) {
    tags = {};
  }

  var metrics = [];
  var browserInfo = getBrowserInfo(self.userAgent);
  var account = self.app.user.get('account');
  var isEnterprise = !!(account !== null && account !== void 0 && account.enterprise);

  for (var name in metricData) {
    var points = metricData[name];
    var metric = {
      metric: name,
      points: points,
      tags: ["browser_domain:" + location.hostname, "browser_path:" + location.pathname, "browser:" + browserInfo.name, "browser_version:" + browserInfo.version, "is_enterprise:" + isEnterprise]
    };
    metric.tags = metric.tags.concat(taggerize(tags));
    addTimestampsToPoints(metric);
    metrics.push(metric);
  }

  return metrics;
};

var handleEvent = function handleEvent(self, eventArgs) {
  var metrics = [];

  for (var _i = 0, _Array$from = Array.from(eventArgs); _i < _Array$from.length; _i++) {
    var _Array$from$_i = _Array$from[_i],
        variable = _Array$from$_i[0],
        value = _Array$from$_i[1],
        opts = _Array$from$_i[2];
    var metric = self.metrics[variable];

    if (metric) {
      var data = _.object([metric], [[value]]);

      metrics.push(toDatadogMetricFormat(self, data, opts));
    } else {
      self.app.trigger('log:warning', "unexpected metric: " + variable, opts);
    }
  }

  postToDatadog(self, 'metrics', _.flatten(metrics));
};
/**
 * Service that consumes `performance` events and sends them to DataDog.
 * Relies on `ddag_url` in env.
 *
 * @example Registering new metrics to send to Datadog
 *   new Service.Datadog @app,
 *     "Presenter load time": "presenter.timing.load.ms"
 *     "Presneter session time": presenter.timing.session.ms"
 *
 *   @app.trigger "performance", "Presenter load time", 250
 *
 * TODO: Make ddag_url a argument you pass into the constructor.
 *
 */


var Datadog =
/**
 * TODO:
 * - add browser.memory.heap.used.mb if available (chrome)
 */

/**
 * @param {Object} app App
 * @param {Object} metrics Custom metrics to track. These extend the built in default metrics
 * @param {Object} options Additional options
 * @param {String} options.userAgent The user agent to use instead of navigator.userAgent
 *
 */
function Datadog(app, metrics, options) {
  if (metrics === void 0) {
    metrics = {};
  }

  if (options === void 0) {
    options = {};
  }

  this.metrics = {
    'Total Time': 'browser.timing.total_page_load.ms',
    'Network Time': 'browser.timing.network.ms',
    'Browser Time': 'browser.timing.browser_processing.ms',
    'Server Time': 'browser.timing.server.ms',
    'DOM Loading': 'browser.timing.dom.loading.ms',
    'DOM Interactive': 'browser.timing.dom.interactive.ms',
    'DOM Loaded': 'browser.timing.dom.loaded.ms',
    'DOM Complete': 'browser.timing.dom.complete.ms',
    'DNS Lookup Time': 'browser.timing.dns.lookup.ms',
    'Connection Time': 'browser.timing.connect.ms',
    'Finished Rendering': 'browser.timing.finished_rendering.ms',
    'CDN Asset Load Time': 'browser.timing.cdn_asset_load_time.ms'
  };
  this.app = app;

  _.extend(this, Backbone.Events); // Allow overriding the userAgent used


  this.userAgent = options.userAgent || navigator.userAgent;

  for (var name in metrics) {
    var metric = metrics[name];

    if (!isValidMetricName(metric)) {
      this.app.trigger('log:warning', "invalid metric syntax: " + metric);
    } else {
      this.metrics[name] = metric;
    }
  }

  this.listenTo(this.app, 'performance', batchCallFunction(1000, _.partial(handleEvent, this)));
};

export { Datadog as default };